import { cn } from "@/lib/utils"
import type { CorteReal as Corte } from "@/routes/finance/mapping"
import { t } from "@/lib/i18n"

/**
 * LA LEYENDA DE LA LÍNEA ROJA, y por qué no basta con pintarla.
 *
 * Cesar: «debería de haber la línea roja de nuestro brandbook que separe las
 * finanzas al corte del mes, trimestre etc, para separar lo real del forecast».
 *
 * La línea la pinta la tabla (`.corte-real` en `index.css`). Esto es lo otro
 * que hace falta: **decir dónde la puso y por qué**. `financial_fact` no marca
 * qué celda es real y cuál proyectada —la hoja de gestión trae los doce meses
 * en filas idénticas—, así que el corte es una DEDUCCIÓN del backend: el último
 * mes con asientos en el libro gerencial, y en el consolidado el de la sociedad
 * más atrasada.
 *
 * Una deducción pintada como una raya es una afirmación que nadie puede
 * comprobar, y en un estado de resultados eso es peor que no tener la raya: se
 * lee como si el sistema supiera algo que no sabe. Por eso la regla viaja desde
 * el backend y se muestra siempre, en el mismo sitio.
 *
 * LOS TRES ESTADOS, y los tres se dicen distinto:
 *
 *   - hay corte      → «Real hasta mayo», con la regla al lado.
 *   - mes = null     → el libro no tiene nada de este año. NO se pinta ninguna
 *                      línea: no hay frontera que trazar.
 *   - ningún bucket  → hay corte, pero agrupado por año (o por semestre) ni uno
 *     completo         solo está entero del lado real. La línea no se puede
 *                      pintar entre columnas, y decir «no hay nada real» sería
 *                      falso. Se dice lo que pasa: baja la granularidad.
 */

const MES = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
             "julio", "agosto", "setiembre", "octubre", "noviembre", "diciembre"]

export function LeyendaDelCorte({
  corte,
  bucketReal,
  className,
}: {
  corte: Corte | undefined
  /** Lo que devolvió `ultimoBucketReal`. `-1` = ninguno entero. */
  bucketReal: number | null
  className?: string
}) {
  if (!corte) return null

  const base = "flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-2xs leading-4"

  if (corte.mes == null) {
    return (
      <p className={cn(base, "text-muted-foreground", className)}>
        <span className="font-medium text-foreground">{t("Todo esto es proyección.")}</span>
        {corte.regla}
      </p>
    )
  }

  return (
    <p className={cn(base, "text-muted-foreground", className)}>
      <span className="inline-flex items-baseline gap-1.5 font-medium text-foreground">
        {/* El filete rojo, en miniatura: es la misma marca que hay en la tabla. */}
        <span
          aria-hidden="true"
          className="inline-block h-2.5 w-[2px] translate-y-px bg-margen"
        />
        {t("Real hasta")}{" "}{MES[corte.mes - 1]}
      </span>
      {bucketReal === -1 ? (
        <span>
          {corte.regla}{" "}{t("Agrupado así no cabe ninguna columna entera del lado real, por eso no hay línea: baja a meses para verla.")}</span>
      ) : (
        <span>{corte.regla}</span>
      )}
    </p>
  )
}
